// Séance de décision : file de travail à gauche (tout ce qui attend l'acte du maire, du président
// ou de leur délégataire), dossier ou courrier au centre, colonne de décision à droite. Raccourcis
// J et K pour parcourir, O pour ouvrir la fiche complète ; les actes sont dans ColonneDecision.
import type { Courrier, Dossier } from '@cc/contrats';
import { EtatErreur, EtatVide, Squelette } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { requete } from '../../api/client';
import { EnTetePage } from '../../commun/EnTetePage';
import { date as formaterDate } from '../../commun/format';
import { ColonneDecision } from './ColonneDecision';
import { VignetteDossier } from './VignetteDossier';
import { VignetteLiquidation } from './VignetteLiquidation';
import { LIBELLES_TYPE, useFileDecisions, type ElementFile } from './file-decisions';
import styles from './decisions.module.css';

type FicheDossier = Dossier & {
  pieces: { id: string; type_piece: string; document_id: string; titre: string; verdict: string; motif: string | null }[];
  transitions_possibles: { code: string; libelle: string; vers: string; motif_obligatoire: boolean }[];
};
type FicheCourrier = Courrier & { pieces?: { id: string; titre: string }[]; transitions_possibles?: { code: string; libelle: string }[] };

function valeurLisible(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Oui' : 'Non';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function Centre({ element }: { element: ElementFile }) {
  const estCourrier = element.type === 'courrier_signature';
  const fiche = useQuery({
    queryKey: [estCourrier ? 'courriers' : 'dossiers', 'fiche', element.id],
    queryFn: ({ signal }) => requete<FicheDossier | FicheCourrier>(`${estCourrier ? '/courriers' : '/dossiers'}/${element.id}`, { signal }),
  });
  if (fiche.isLoading) return <Squelette lignes={6} />;
  if (fiche.isError || !fiche.data) return <EtatErreur message="La fiche n'a pas pu être chargée." onReessayer={() => void fiche.refetch()} />;

  if (estCourrier) {
    const c = fiche.data as FicheCourrier;
    return (
      <article className={styles.dossier} aria-live="polite">
        <div className={styles.enTete}>
          <p className="cc-etiquette">{LIBELLES_TYPE[element.type]} · courrier départ</p>
          <h2>{c.objet}</h2>
          <div className={styles.meta}>
            <span className="cc-mono">{c.numero ?? 'Sans numéro'}</span>
            <span>À : {c.correspondant_nom}</span>
            {c.service_affectataire?.nom ? <span>Service : {c.service_affectataire.nom}</span> : null}
            {c.echeance_interne ? <span>Échéance interne : {formaterDate(c.echeance_interne)}</span> : null}
          </div>
        </div>
        <section className={styles.bloc}>
          <h3>Pièces</h3>
          {c.pieces?.length ? (
            <ul className={styles.pieces}>
              {c.pieces.map((p) => (
                <li key={p.id} className={styles.piece}>
                  <span>{p.titre}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.sans}>Aucune pièce jointe.</p>
          )}
        </section>
      </article>
    );
  }

  const d = fiche.data as FicheDossier;
  const donnees = Object.entries(d.donnees_formulaire ?? {});
  return (
    <article className={styles.dossier} aria-live="polite">
      <div className={styles.enTete}>
        <p className="cc-etiquette">
          {LIBELLES_TYPE[element.type]} · {d.demarche.libelle}
        </p>
        <h2>{d.demandeur.nom ?? d.demarche.libelle}</h2>
        <div className={styles.meta}>
          <span className="cc-mono">{d.numero ?? 'Sans numéro'}</span>
          {d.service ? <span>Service : {d.service.nom}</span> : null}
          {d.enregistre_le ? <span>Enregistré le {formaterDate(d.enregistre_le)}</span> : null}
          {d.echeance_interne ? <span className={d.en_retard ? styles.retard : undefined}>Échéance interne : {formaterDate(d.echeance_interne)}</span> : null}
          {d.echeance_reglementaire ? <span>Réglementaire : {formaterDate(d.echeance_reglementaire)}</span> : null}
        </div>
      </div>
      {d.localisation ? <VignetteDossier dossier={d} /> : null}
      <section className={styles.bloc}>
        <h3>Pièces justificatives</h3>
        {d.pieces?.length ? (
          <ul className={styles.pieces}>
            {d.pieces.map((p) => (
              <li key={p.id} className={styles.piece}>
                <span>{p.titre}</span>
                <span className={p.verdict === 'conforme' ? styles.conforme : p.verdict === 'non_conforme' ? styles.nonConforme : styles.nonExaminee}>
                  {p.verdict === 'conforme' ? 'Conforme' : p.verdict === 'non_conforme' ? 'Non conforme' : 'Non examinée'}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.sans}>Aucune pièce.</p>
        )}
      </section>
      <section className={styles.bloc}>
        <h3>Formulaire</h3>
        {donnees.length ? (
          <dl className={styles.donnees}>
            {donnees.map(([cle, valeur]) => (
              <div key={cle} style={{ display: 'contents' }}>
                <dt>{cle}</dt>
                <dd>{valeurLisible(valeur)}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className={styles.sans}>Aucune donnée saisie.</p>
        )}
      </section>
    </article>
  );
}

export function PageDecisions() {
  const file = useFileDecisions();
  const navigate = useNavigate();
  const [courant, setCourant] = useState<string | null>(null);
  const elements = file.elements;
  const index = useMemo(() => elements.findIndex((e) => e.id === courant), [elements, courant]);
  const element = index >= 0 ? elements[index] : (elements[0] ?? null);

  useEffect(() => {
    if (!courant && elements.length) setCourant(elements[0]?.id ?? null);
    if (courant && !elements.some((e) => e.id === courant)) setCourant(elements[0]?.id ?? null);
  }, [elements, courant]);

  useEffect(() => {
    const touche = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const cible = e.target as HTMLElement | null;
      if (cible && (cible.tagName === 'INPUT' || cible.tagName === 'TEXTAREA' || cible.isContentEditable)) return;
      const k = e.key.toLowerCase();
      if (k === 'j' && index < elements.length - 1) setCourant(elements[index + 1]?.id ?? null);
      else if (k === 'k' && index > 0) setCourant(elements[index - 1]?.id ?? null);
      else if (k === 'o' && element) navigate(element.type === 'mandat_a_ordonnancer' ? '/budget/depense' : element.type === 'courrier_signature' ? `/courriers/${element.id}` : `/dossiers/${element.id}`);
    };
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
  }, [elements, index, element, navigate]);

  const fiche = useQuery({
    queryKey: [element?.type === 'courrier_signature' ? 'courriers' : 'dossiers', 'fiche', element?.id ?? ''],
    queryFn: ({ signal }) => requete<FicheDossier | FicheCourrier>(`${element?.type === 'courrier_signature' ? '/courriers' : '/dossiers'}/${element?.id}`, { signal }),
    enabled: Boolean(element) && element?.type !== 'mandat_a_ordonnancer',
  });
  const transitions = (fiche.data as FicheDossier | undefined)?.transitions_possibles ?? [];

  return (
    <>
      <EnTetePage
        titre="Séance de décision"
        etiquette="Décisions"
        description={
          file.chargement && elements.length === 0 ? 'Chargement de la file…' : elements.length ? `${elements.length} élément${elements.length > 1 ? 's' : ''} attendent votre acte.` : 'Rien n\'attend votre acte.'
        }
      />
      {file.erreur ? <EtatErreur message="La file de décision n'a pas pu être chargée." onReessayer={file.recharger} /> : null}
      {file.partiel ? <p className={styles.sans}>Une partie de la file n&apos;a pas pu être chargée (droits ou module inactif).</p> : null}
      <div className={styles.seance}>
        <section className={styles.file} aria-label="File du jour">
          <div className={styles.fileEntete}>
            <h2>File du jour</h2>
            <span className="cc-mono">{elements.length}</span>
          </div>
          {file.chargement && elements.length === 0 ? (
            <div style={{ padding: 14 }}>
              <Squelette lignes={5} />
            </div>
          ) : elements.length === 0 ? (
            <div className={styles.vide}>
              <EtatVide titre="Rien à décider">Aucun dossier en attente de décision ni courrier à signer dans votre périmètre.</EtatVide>
            </div>
          ) : (
            <ul className={styles.fileListe}>
              {elements.map((e) => (
                <li key={`${e.type}-${e.id}`}>
                  <button type="button" className={styles.element} aria-current={element?.id === e.id ? 'true' : undefined} onClick={() => setCourant(e.id)}>
                    <span className={styles.reference}>{e.reference}</span>
                    {e.enRetard ? <span className={styles.retard}>En retard</span> : e.echeance ? <span className={styles.reference}>{formaterDate(e.echeance)}</span> : null}
                    <span className={styles.titre}>{e.titre}</span>
                    {e.sousTitre ? <span className={styles.sousTitre}>{e.sousTitre}</span> : null}
                    <span className={styles.type}>{LIBELLES_TYPE[e.type]}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
        {element ? element.type === 'mandat_a_ordonnancer' ? <VignetteLiquidation id={element.id} /> : <Centre element={element} /> : <div />}
        {element ? (
          <ColonneDecision
            element={element}
            transitions={transitions}
            raccourcisActifs
            onActe={() => {
              const suivant = elements[index + 1] ?? elements[index - 1] ?? null;
              setCourant(suivant?.id ?? null);
            }}
          />
        ) : null}
      </div>
    </>
  );
}
